import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NotificationService } from '../../services/notification.service';
import { ChampObligatoire, InformationChampsObligatoires, ProgressionService } from './progression.service';

@Component({
  selector: 'progression',
  templateUrl: './progression.component.html',
  styleUrls: ['./progression.component.css'],
})
export class ProgressionComponent<T> {
  @Input() champsObligatoires: ChampObligatoire<T>[];
  @Input() value: T;
  @Input() editMode: boolean = false;
  @Input() showProgressBar: boolean = true;
  @Input() showDetails: boolean = true;
  @Input() activeTab: string;
  @Input() activeTabIndex: number;
  @Input() questionIdToShowErreur: number = null;
  @Input() displayAllChampObligatoire: boolean = false;
  @Input() data: any;
  @Input() showProgression: boolean = true;

  @Output() activeTabChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() activeTabIndexChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() questionIdToShowErreurChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() displayAllChampObligatoireChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() collapseIndexOpen: EventEmitter<number> = new EventEmitter<number>();
  @Output() collapseGroupeIndexOpen: EventEmitter<number> = new EventEmitter<number>();

  informationChampsObligatoires: InformationChampsObligatoires<T>;
  indexChampObligatoire: number = -1;
  memoChampsObligatoiresManquants: ChampObligatoire<T> = null;

  constructor(
    public progressionService: ProgressionService,
    private notificationService: NotificationService,
  ) {}

  public get valueCompletion(): number {
    let returnValue: number = 0;
    if (this.value) {
      this.informationChampsObligatoires = this.progressionService.getInformationChampsObligatoires(this.champsObligatoires, this.value, this.data);
      returnValue = this.informationChampsObligatoires.valueCompletion;
    }
    return returnValue;
  }

  public get champsObligatoiresManquants(): string {
    return this.champsObligatoiresManquantsJoined(';');
  }

  public champsObligatoiresManquantsJoined(separator: string, displayFirstSeparator: boolean = false): string {
    return this.informationChampsObligatoires.champsObligatoiresManquants.length > 0 ? (displayFirstSeparator ? separator : '') + this.informationChampsObligatoires.champsObligatoiresManquants.map(m => m.champ).join(separator) : null;
  }

  showErreur(step: number) {
    if (this.displayAllChampObligatoire) {
      this.displayAllChampObligatoire = false;
      this.displayAllChampObligatoireChange.emit(this.displayAllChampObligatoire);
    }

    if (this.memoChampsObligatoiresManquants != null && this.memoChampsObligatoiresManquants.check(this.value, this.data) && step == 1) {
      // Le champ en cours a été renseigné, le suivant doit ocnserver le même index, donc step = 0
      step = 0;
    }

    this.indexChampObligatoire = this.indexChampObligatoire + step;
    if (this.indexChampObligatoire < 0) {
      this.indexChampObligatoire = this.informationChampsObligatoires.champsObligatoiresManquants.length - 1;
    } else if (this.indexChampObligatoire >= this.informationChampsObligatoires.champsObligatoiresManquants.length) {
      this.indexChampObligatoire = 0;
    }
    let messageCompletion = this.informationChampsObligatoires.champsObligatoiresManquants[this.indexChampObligatoire];
    this.activeTab = messageCompletion.tab;
    this.activeTabChange.emit(this.activeTab);
    this.activeTabIndex = messageCompletion.tabIndex;
    this.activeTabIndexChange.emit(this.activeTabIndex);
    this.collapseIndexOpen.emit(messageCompletion.collapseIndex);
    this.collapseGroupeIndexOpen.emit(messageCompletion.collapseGroupeIndex);
    this.questionIdToShowErreur = messageCompletion.idQuestion;
    this.questionIdToShowErreurChange.emit(this.questionIdToShowErreur);

    // Memo
    this.memoChampsObligatoiresManquants = this.informationChampsObligatoires.champsObligatoiresManquants[this.indexChampObligatoire];

    if (this.informationChampsObligatoires.champsObligatoiresManquants[this.indexChampObligatoire].id) {
      let top = document.getElementById(this.informationChampsObligatoires.champsObligatoiresManquants[this.indexChampObligatoire].id);
      if (top !== null) {
        setTimeout(() => {
          top.scrollIntoView();
        }, 100);
      }
    }
  }

  hideAllErrors() {
    if (this.displayAllChampObligatoire) {
      this.displayAllChampObligatoire = false;
      this.displayAllChampObligatoireChange.emit(this.displayAllChampObligatoire);
    }
    this.indexChampObligatoire = -1;
    this.questionIdToShowErreur = -1;
    this.questionIdToShowErreurChange.emit(this.questionIdToShowErreur);
  }

  showAllErrors() {
    this.indexChampObligatoire = -1;
    if (this.informationChampsObligatoires.champsObligatoiresManquants.length > 0) {
      if (!this.displayAllChampObligatoire) {
        this.displayAllChampObligatoire = true;
        this.displayAllChampObligatoireChange.emit(this.displayAllChampObligatoire);
      }
    } else {
      this.notificationService.success('Tous les champs obligatoires sont renseignés.');
    }
  }

  showNextErreur() {
    this.showErreur(1);
  }

  showPreviousErreur() {
    this.showErreur(-1);
  }

  showActivatedIfErreur(...keys: string[]): boolean {
    return keys.some(
      key =>
        (this.indexChampObligatoire > -1 && this.indexChampObligatoire < this.informationChampsObligatoires.champsObligatoiresManquants.length && this.informationChampsObligatoires.champsObligatoiresManquants[this.indexChampObligatoire].key === key) ||
        (this.displayAllChampObligatoire && this.informationChampsObligatoires.champsObligatoiresManquants.some(m => m.key === key)),
    );
  }

  get tooltip(): string {
    let tooltip = 'Taux de remplissage : ' + this.valueCompletion + ' %<br/>Informations obligatoires restants à renseigner : ' + this.informationChampsObligatoires?.champsObligatoiresManquants?.length;
    if (this.showDetails && this.champsObligatoiresManquants != null && this.champsObligatoiresManquants.length > 0) {
      tooltip += '<br/><br/>Détails :' + this.champsObligatoiresManquantsJoined('<br/>- ', true);
    }
    return tooltip;
  }
}
